import { forwardRef, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Stack, Box } from '@mui/material';
import { QuoteStatus, TQuoteApiResponse } from '@models/quotes';
import { Alert } from '@components/alert/Alert';
import { getQuotePolicyAlerts } from '@pages/QuoteDetails/helpers/getQuotePolicyAlerts';
import { Action, QuoteStatusAlertInfo } from '@pages/QuoteDetails/constants/QuoteDetails.constants';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { ImpersonationAlertBanner } from '@pages/QuoteDetails/components/ImpersonationAlertBanner';
import { ToastType } from '@components/toast';
import { useSnackbar } from 'notistack';
import { useUpdateQuote } from '@services/quotes/useUpdateQuote';
import { IConfirmDialog } from '@pages/QuoteDetails/types/ConfirmDialog.interface';
import { ConfirmationDialog } from '@components/dialog/ConfirmationDialog';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { AlertStatus } from '@models/alert';
import { DuplicateQuoteDialog } from '@pages/QuoteDetails/dialogs';
import { ReactivateQuoteDialogWrapper } from '@components/modals/ReactivateQuoteModals/ReactivateQuoteDialogWrapper';
import { useGetPolicy, useSearchPolicyTransactions } from '@services/policy';
import { getFormattedPolicyNumber } from '@utils/policy/getFormattedPolicyNumber';
import { useQuoteMoratoriums } from '@services/quotes/moratorium/useQuoteMoratoriums';
import { PolicyTransactionStatus, PolicyTransactionTypes } from '@models/policyTransaction';
import { useSearchQuoteForms } from '@services/quoteForm';
import { useSearchFormVersions } from '@services/formVersion';

export type QuoteDetailPolicyAlertBannerProps = {
  quote: TQuoteApiResponse;
  customer: {
    matchState?: string;
  };
  isPolicyMarkedForNonrenewal?: boolean;
  isReadOnlyStatus: boolean;
  isInternalUser: boolean;
  isUnderwriter: boolean;
  isLIOContractor: boolean;
  onOpenReactivateQuoteDialog: () => void;
};

export const QuoteDetailPolicyAlertBanner = forwardRef(
  (
    {
      quote,
      isPolicyMarkedForNonrenewal = false,
      isReadOnlyStatus,
      isInternalUser,
      isUnderwriter,
      isLIOContractor,
      customer,
      onOpenReactivateQuoteDialog,
    }: QuoteDetailPolicyAlertBannerProps,
    ref,
  ) => {
    const { updateQuote } = useUpdateQuote();
    const { enqueueSnackbar } = useSnackbar();
    const { isMobileScreen } = useGetScreenSize();
    const [impersonation] = useStore((state) => [state.impersonation, state.setUserRoles], shallow);
    const [confirmationDialogState, setConfirmationDialogState] = useState<IConfirmDialog | null>(null);

    const [{ isJrOps, isSrOps, isBroker }] = useStore((state) => [state.userAuth], shallow);

    const { data: policy } = useGetPolicy({
      policyId: isInternalUser && quote ? quote.policyId : undefined,
    });
    const shouldCheckRenewalTransactions = [
      QuoteStatus.Canceled.id,
      QuoteStatus.Declined.id,
      QuoteStatus.Rejected.id,
    ].includes(quote?.status?.id);
    const { policyTransactions: nonrenewalPolicyTransactions } = useSearchPolicyTransactions(
      {
        policyId: quote?.policyId ?? '',
        transactionType: PolicyTransactionTypes.DoNotRenew,
        status: [PolicyTransactionStatus.Created, PolicyTransactionStatus.Processed],
        includeCanceled: false,
        pageLimit: 1,
        pageOffset: 0,
      },
      !!quote?.policyId && shouldCheckRenewalTransactions,
    );

    const effectiveIsPolicyMarkedForNonrenewal =
      isPolicyMarkedForNonrenewal || (nonrenewalPolicyTransactions?.items?.length ?? 0) > 0;

    const navigate = useNavigate();
    const quoteStatusAlertName = quote?.status?.name ?? '';
    const { quoteForms } = useSearchQuoteForms({ quoteId: quote?.id ?? '' });
    const { formVersions } = useSearchFormVersions({ quoteId: quote?.id ?? '' });
    const hasFormConflicts = useMemo(() => {
      if (!isInternalUser) return false;
      const activeForms = quoteForms?.items?.filter((qf) => qf.isOnQuote) ?? [];
      if (activeForms.length < 2) return false;

      const conflictMap = new Map<string, Set<string>>();
      formVersions?.items?.forEach((fv) => {
        conflictMap.set(fv.formV2.id, new Set(fv.formConflicts.map((conflict) => conflict.id)));
      });

      const activeFormIds = new Set(activeForms.map((qf) => qf.formVersion.form.id));
      return activeForms.some((qf) => {
        const conflicts = conflictMap.get(qf.formVersion.form.id);
        if (!conflicts) return false;
        return Array.from(conflicts).some((conflictId) => activeFormIds.has(conflictId));
      });
    }, [quoteForms, formVersions, isInternalUser]);

    const isSidebarOpen = window.location.search.includes('sidebarItem');

    const customerIsConfirmedMatch = customer?.matchState === 'Confirmed';

    // determine if the user is in a safari browser (and then yell at them!)
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    let leftWidth = '0px';
    if (isSafari) {
      leftWidth = isSidebarOpen ? '335px' : '50px';
    }

    const [duplicateQuoteDetails, setDuplicateQuoteDetails] = useState<{ quoteId?: string }>({
      quoteId: undefined,
    });
    const [declinedRejectedQuoteDetails, setReactivateDeclinedRejectedQuoteDetails] = useState<{ quoteId?: string }>({
      quoteId: undefined,
    });

    const handleOpenDuplicateQuoteDialog = (quoteId: string) => {
      setDuplicateQuoteDetails({ quoteId });
    };
    const handleCloseDuplicateQuoteDialog = () => {
      setDuplicateQuoteDetails({ quoteId: undefined });
    };
    const handleOpenReactivateDeclinedRejectedQuoteDialog = (quoteId: string) => {
      setReactivateDeclinedRejectedQuoteDetails({ quoteId });
    };
    const handleCloseReactivateDeclinedRejectedQuoteDialog = () => {
      setReactivateDeclinedRejectedQuoteDetails({ quoteId: undefined });
    };

    // Get moratorium banner data
    const { bannerData: moratoriumBanner } = useQuoteMoratoriums(quote?.id);

    const quotePolicyAlerts = useMemo(() => {
      const baseAlerts =
        getQuotePolicyAlerts({
          quote,
          isJrOps,
          isSrOps,
          isBroker,
          isInternalUser,
          isUnderwriter,
          isLIOContractor,
          isReadOnlyStatus,
          isPolicyMarkedForNonrenewal: effectiveIsPolicyMarkedForNonrenewal,
          hasFormConflicts,
        }) ?? [];

      // Final safety guard: when policy is marked for nonrenewal, never show reactivation CTA/message.
      if (
        effectiveIsPolicyMarkedForNonrenewal &&
        [QuoteStatus.Canceled.name, QuoteStatus.Declined.name, QuoteStatus.Rejected.name].includes(quoteStatusAlertName)
      ) {
        for (const alert of baseAlerts) {
          if (alert.ctaText === 'Reactivate Quote') {
            alert.ctaText = undefined;
          }
          if (alert.message === 'This quote has been cancelled but can be reactivated.') {
            alert.message = 'This quote has been cancelled.';
          }
        }
      }

      // Add moratorium alert at the beginning (highest priority)
      if (moratoriumBanner) {
        baseAlerts.unshift({
          severity: moratoriumBanner.severity,
          message: moratoriumBanner.message,
        });
      }

      if (!customerIsConfirmedMatch) {
        baseAlerts.push({
          severity: AlertStatus.Warning,
          message: 'This customer is not yet approved. This quote cannot be bound until this is reconciled.',
        });
      }

      return baseAlerts;
    }, [
      quote,
      customerIsConfirmedMatch,
      isJrOps,
      isSrOps,
      isBroker,
      isInternalUser,
      isUnderwriter,
      isLIOContractor,
      isReadOnlyStatus,
      effectiveIsPolicyMarkedForNonrenewal,
      quoteStatusAlertName,
      moratoriumBanner,
      hasFormConflicts,
    ]);

    const handleCtaFunction = async (action?: string) => {
      if (action) {
        switch (action) {
          case Action.ViewDuplicateQuotes:
            handleOpenDuplicateQuoteDialog(quote.id);
            break;
          case Action.ReactivateDeclinedRejectedQuote:
            handleOpenReactivateDeclinedRejectedQuoteDialog(quote.id);
            break;
          case Action.ReleaseQuoteToPolicyHolder:
            setConfirmationDialogState({
              show: true,
              confirmText: 'Confirm',
              cancelText: 'Exit',
              navigatePath: '?',
              description: 'Are you sure you want to release this quote to the policyholder?',
              secondaryDescription: 'Once confirmed, the policyholder will be notified to review and bind the quote.',
              onConfirmation: () => {
                updateQuote({ quoteId: quote.id, releasedToPolicyHolder: true }).then(() => {
                  enqueueSnackbar(`Successfully marked quote as released to policyholder`, {
                    preventDuplicate: true,
                    variant: ToastType.Success,
                  });

                  setConfirmationDialogState(null);
                });
              },
            });
            break;
          default:
            break;
        }

        return;
      }
      if (quote !== undefined) {
        if (quoteStatusAlertName === QuoteStatus.Bound.name && quote.policyNumber) {
          navigate(`/policy/${quote.policyId}`);
        } else if (
          (quoteStatusAlertName === QuoteStatus.Canceled.name || quoteStatusAlertName === QuoteStatus.Expired.name) &&
          !effectiveIsPolicyMarkedForNonrenewal
        ) {
          onOpenReactivateQuoteDialog();
        } else {
          QuoteStatusAlertInfo[quoteStatusAlertName]?.ctaFunction?.();
        }
      }
    };

    return quote && (quotePolicyAlerts || impersonation) ? (
      <Box sx={{ position: 'relative', height: '20px', width: '100%' }}>
        <ConfirmationDialog
          isOpen={confirmationDialogState?.show ?? false}
          onCancel={() => setConfirmationDialogState(null)}
          description={confirmationDialogState?.description ?? ''}
          secondaryDescription={confirmationDialogState?.secondaryDescription}
          buttonConfig={{
            onConfirm: confirmationDialogState?.onConfirmation ?? (() => {}),
            confirmText: confirmationDialogState?.confirmText ?? '',
            secondaryText: confirmationDialogState?.cancelText ?? '',
          }}
        />
        <Stack
          id='AlertList'
          ref={ref as any}
          sx={
            isMobileScreen
              ? {
                  width: '100vw',
                  position: 'fixed',
                  top: 'unset',
                  zIndex: '1',
                }
              : {
                  zIndex: '1',
                  marginBottom: '30px',
                  position: 'fixed',
                  width: 'fill-available; -webkit-fill-available',
                }
          }
        >
          {impersonation && (
            <ImpersonationAlertBanner otherVisibleAlerts={Boolean(quotePolicyAlerts && quotePolicyAlerts.length > 0)} />
          )}
          {quote?.continuedFromPolicyId && quote?.continuedFromPolicy && (
            <Alert
              data-testid='quoteContinuationAlert'
              type={AlertStatus.Info}
              variant='filled'
              sx={{ marginBottom: '1px' }}
            >
              This quote is a continuation of policy{' '}
              <Link to={`/policy/${quote.continuedFromPolicy.id}`}>
                {getFormattedPolicyNumber(
                  quote.continuedFromPolicy.policyNumberPrefix,
                  quote.continuedFromPolicy.policyNumber.policyNumber,
                )}
              </Link>
            </Alert>
          )}
          {quotePolicyAlerts?.map((quotePolicyAlert, index) => (
            <Alert
              key={quotePolicyAlert.message}
              data-testid='quotePolicyAlert'
              type={quotePolicyAlert.severity}
              variant='filled'
              ctaText={quotePolicyAlert?.ctaText}
              ctaUrl={quotePolicyAlert?.ctaUrl}
              ctaDisabled={quotePolicyAlert?.ctaDisabled}
              ctaHoverText={quotePolicyAlert?.ctaHoverText}
              ctaFunction={() => {
                if (quotePolicyAlert?.ctaAction) {
                  handleCtaFunction(quotePolicyAlert.ctaAction());
                } else {
                  handleCtaFunction();
                }
              }}
              sx={{ marginBottom: index !== quotePolicyAlerts.length - 1 ? '1px' : 'auto' }}
              ctaSxProps={
                isSafari
                  ? {
                      right: leftWidth,
                    }
                  : undefined
              }
            >
              {quotePolicyAlert.message}
            </Alert>
          ))}
        </Stack>
        <DuplicateQuoteDialog
          showDialog={Boolean(duplicateQuoteDetails && duplicateQuoteDetails?.quoteId)}
          quoteId={quote?.id}
          closeLinkDialog={handleCloseDuplicateQuoteDialog}
          isUpdateQuoteLoading={!quote}
        />

        {declinedRejectedQuoteDetails && !!declinedRejectedQuoteDetails?.quoteId && (
          <ReactivateQuoteDialogWrapper
            showReactivateDialog={false}
            declinedRejectedQuoteDetails={{
              declinedRejectedQuotePolicyNumber: quote?.policyNumber?.policyNumber ?? '',
              declinedRejectedQuoteId: declinedRejectedQuoteDetails.quoteId ?? '',
              declinedRejectedQuoteStatus: quote?.status?.name ?? '',
            }}
            policyNumber={
              policy
                ? getFormattedPolicyNumber(policy?.policyNumberPrefix, policy?.policyNumber.policyNumber)
                : (quote.policyNumber?.policyNumber ?? '')
            }
            hideActionDialog={() => {}}
            handleCloseReactivateDeclinedRejectedQuoteDialog={handleCloseReactivateDeclinedRejectedQuoteDialog}
            quote={quote}
            onStatusRead={() => {}}
          />
        )}
      </Box>
    ) : null;
  },
);

QuoteDetailPolicyAlertBanner.displayName = 'QuoteDetailPolicyAlertBanner';
